// @ts-check
/**
 * Converts a wall-clock date + time, meant to be read in an arbitrary IANA timezone, into the
 * UTC instant it refers to - without a timezone library. The admin picks a date, a time and a
 * zone (Intl.supportedValuesOf('timeZone')); the backend only ever receives one unambiguous ISO
 * instant, so no timezone logic lives server-side.
 *
 * The two-pass trick: read the wall-clock date/time as if it WERE UTC (a first guess), then ask
 * Intl what wall-clock that guessed instant actually shows in the target zone. The difference
 * between those two readings is the zone's offset from UTC at that moment; subtracting it from
 * the guess gives the true UTC instant. Accurate except within the hour around a DST transition
 * in the chosen zone, an acceptable edge case for scheduling a campaign.
 *
 * @param {string} dateStr "2026-10-03"
 * @param {string} timeStr "11:00"
 * @param {string} timeZone an IANA zone name, e.g. "Asia/Kolkata"
 * @returns {string} ISO 8601 instant in UTC
 */
export function zonedTimeToUtcIso(dateStr, timeStr, timeZone) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(new Date(guess)).map((p) => [p.type, p.value]));
  const asIfUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second),
  );
  const offset = asIfUtc - guess;
  return new Date(guess - offset).toISOString();
}

/** Every IANA zone the browser knows, alphabetical; falls back to a short fixed list where
 * Intl.supportedValuesOf is unavailable (very old browsers only). The browser's own zone
 * (browserTimeZone()) is prepended when it is not already in the list, so a `<select>`
 * initialized to it always has a matching `<option>` - otherwise the controlled select can
 * visually show a different zone than the state (and the schedule) actually holds. */
export function supportedTimeZones() {
  let zones;
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = ['UTC', 'Asia/Kolkata', 'America/New_York', 'Europe/London'];
  }
  const ownZone = browserTimeZone();
  return zones.includes(ownZone) ? zones : [ownZone, ...zones];
}

/** The viewer's own IANA zone, used as the schedule picker's default. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
